import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CampaignApi } from '../services/CampaignService';

const TRIGGER_COMMENT = 'comment_on_publication';
const ACTION_SEND_DM = 'send_dm';

export default function InstagramCampaignDetail() {
  const { campaignId } = useParams();
  const id = Number(campaignId);

  const [campaign, setCampaign] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [fileId, setFileId] = useState('');
  const [rename, setRename] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [detail, fileRows] = await Promise.all([
        CampaignApi.get(id),
        CampaignApi.listFiles(),
      ]);
      setCampaign(detail);
      setRename(detail?.name || '');
      setFiles(fileRows || []);
      setFileId((prev) => {
        if (prev) return prev;
        return fileRows?.length ? String(fileRows[0].id) : '';
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load');
    }
  }, [id]);

  useEffect(() => {
    if (Number.isFinite(id)) {
      load();
    }
  }, [id, load]);

  const saveName = async () => {
    setBusy(true);
    setError('');
    try {
      await CampaignApi.rename(id, rename.trim());
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Rename failed');
    } finally {
      setBusy(false);
    }
  };

  const runLifecycle = async (action) => {
    setBusy(true);
    setError('');
    try {
      if (action === 'ready') await CampaignApi.markReady(id);
      if (action === 'clear') await CampaignApi.clearReady(id);
      if (action === 'deactivate') await CampaignApi.deactivate(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Lifecycle failed');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const saved = await CampaignApi.uploadFile(file);
      setFileId(String(saved.id));
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const addUseCase = async () => {
    if (!keyword.trim() || !fileId) return;
    setBusy(true);
    setError('');
    try {
      await CampaignApi.createUseCase(id, {
        triggerType: TRIGGER_COMMENT,
        triggerValue: keyword.trim(),
        actionType: ACTION_SEND_DM,
        actionValue: String(fileId),
      });
      setKeyword('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'UseCase create failed');
    } finally {
      setBusy(false);
    }
  };

  const removeUseCase = async (useCaseId) => {
    if (!window.confirm('Delete this Use Case?')) return;
    setBusy(true);
    setError('');
    try {
      await CampaignApi.deleteUseCase(id, useCaseId);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (!campaign && !error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Button
        component={RouterLink}
        to="/teacher/bot/instagram/campaigns"
        size="small"
        sx={{ mb: 1 }}
      >
        ← Campaigns
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">{campaign?.name || 'Campaign'}</Typography>
        {campaign && <Chip label={campaign.status} />}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {Array.isArray(error) ? error.join(', ') : String(error)}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Campaign
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={rename}
            onChange={(e) => setRename(e.target.value)}
          />
          <Button variant="outlined" disabled={busy} onClick={saveName}>
            Save name
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(campaign?.status === 'new' || campaign?.status === 'inactive') && (
            <Button disabled={busy} onClick={() => runLifecycle('ready')}>
              Mark READY
            </Button>
          )}
          {campaign?.status === 'ready' && (
            <Button disabled={busy} onClick={() => runLifecycle('clear')}>
              Clear READY
            </Button>
          )}
          {campaign?.status === 'active' && (
            <Button
              color="warning"
              disabled={busy}
              onClick={() => runLifecycle('deactivate')}
            >
              Deactivate
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Attached Instagram media
        </Typography>
        {(campaign?.media || []).length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            None yet. When status is READY, the first comment on a media id
            attaches it and moves the campaign to ACTIVE.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Media ID</TableCell>
                <TableCell>Attached</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.media.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.instagramMediaId}</TableCell>
                  <TableCell>
                    {m.attachedAt ? new Date(m.attachedAt).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          PDF files (for send_dm)
        </Typography>
        <Button variant="outlined" component="label" disabled={busy} sx={{ mb: 2 }}>
          Upload PDF
          <input hidden type="file" accept="application/pdf" onChange={onUpload} />
        </Button>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Filename</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((f) => (
              <TableRow key={f.id}>
                <TableCell>{f.id}</TableCell>
                <TableCell>{f.originalFilename}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Use Cases
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Trigger: comment contains keyword (case-insensitive). Action: send DM
          with download link for the selected PDF.
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2 }}
        >
          <TextField
            label="Keyword"
            size="small"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="PDF file"
            size="small"
            value={fileId}
            onChange={(e) => setFileId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {files.length === 0 && (
              <MenuItem value="" disabled>
                Upload a PDF first
              </MenuItem>
            )}
            {files.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                #{f.id} {f.originalFilename}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={busy || !keyword.trim() || !fileId}
            onClick={addUseCase}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Use Case
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Keyword</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>File id</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(campaign?.useCases || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">No Use Cases yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {(campaign?.useCases || []).map((uc) => (
              <TableRow key={uc.id}>
                <TableCell>{uc.id}</TableCell>
                <TableCell>{uc.triggerType}</TableCell>
                <TableCell>{uc.triggerValue}</TableCell>
                <TableCell>{uc.actionType}</TableCell>
                <TableCell>{uc.actionValue}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={() => removeUseCase(uc.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
